import React from 'react';
import { get } from 'lodash/fp';
import { checkNullData, generateDFSClientId } from 'utils';
import { NA } from 'utils/formatting';
import { SkeletonLine } from '../../../ui-library';

const translationCommunicationMap: Record<string, string> = {
    additionalChannelToUsePortal: 'additionalCommunicationMethods.additionalCommunicationChannelToUse.portal',
    additionalChannelToUseEmail: 'additionalCommunicationMethods.additionalCommunicationChannelToUse.email',
    additionalChannelToUsePaper: 'additionalCommunicationMethods.additionalCommunicationChannelToUse.paper',
};

type OptionItem = { id?: string | number; label?: string };
type NavigateFn = (path: string, state: { clientName: string; isProspect: boolean }) => void;
type TranslationFn = (key: string) => string;

type AdaptClientListProps = {
    navigate: NavigateFn;
    data?: Record<string, unknown>[];
    t: TranslationFn;
    columns?: Array<{ key: string; render?: unknown }>;
    isLoading?: boolean;
    getFormattedDate: (value: unknown) => string;
    optionGender?: OptionItem[];
    optionLanguage?: OptionItem[];
    optionCountry?: OptionItem[];
    pageSize?: number;
};

const getWithDefault = (
    obj: Record<string, unknown> = {},
    path = '',
    formatting: (value: unknown) => unknown = (v) => v,
): unknown => {
    const value = get(path, obj);

    if (![undefined, null].includes(value)) return formatting(get(path, obj));

    return NA;
};

const findOptionById = (optionsList: OptionItem[] = [], id: string | number) => (optionsList?.find((item) => ((item?.id)) === id));

export const adaptClientList = ({
    navigate,
    data = [],
    t,
    columns = [],
    isLoading = false,
    getFormattedDate,
    optionGender = [],
    optionLanguage = [],
    optionCountry = [],
    pageSize = 10,
}: AdaptClientListProps): Record<string, unknown>[] => {
    if (isLoading) {
        return new Array(pageSize).fill(null).map(() => (
            columns.map(({ key, render }) => {
                if (render) {
                    return ({
                        [key]: {
                            label: <SkeletonLine width="100px" />,
                            render: <SkeletonLine width="100%" />,
                        },
                    });
                }

                return ({ [key]: <SkeletonLine width="100%" /> });
            }).reduce((a, i) => ({ ...a, ...i }), {})
        ));
    }

    if (checkNullData(data)) return [];

    const clientOverview = ({ internalId, clientName }: { internalId: string; clientName: string }) => () => {
        navigate(`/client/${internalId}`, { clientName, isProspect: false });
    };

    const onBoarding = ({ internalId, clientName }: { internalId: string; clientName: string }) => () => {
        navigate(`/onboarding/${internalId}`, { clientName, isProspect: true });
    };

    return data.map((item) => {
        const internalId = generateDFSClientId(item.id);
        const clientName = `${getWithDefault(item, 'firstName')} ${getWithDefault(item, 'lastName')}`;
        const isProspect = false;

        const communication = ['additionalChannelToUsePortal', 'additionalChannelToUseEmail', 'additionalChannelToUsePaper'].reduce(
            (accu: string[], communicationPath: string) => {
                if (get(communicationPath, item)) {
                    accu.push(t(translationCommunicationMap[communicationPath]));
                }

                return accu;
            },
            [],
        ).join(', ');

        return {
            id: item.id,
            primaryEmail: getWithDefault(item, 'email'),
            addressLineThree: getWithDefault(item, 'street'),
            postcode: getWithDefault(item, 'postCode'),
            location: getWithDefault(item, 'city'),
            dateOfBirth: getFormattedDate(getWithDefault(item, 'birthDate')),
            language: getWithDefault(findOptionById(optionLanguage, parseInt(String(item.language), 10)), 'label'),
            ahvNo: getWithDefault(item, 'ahvNo'),
            gender: getWithDefault(findOptionById(optionGender, String(item.gender)), 'label'),
            communication: communication.length === 0 ? NA : communication,
            primaryPhoneNumber: getWithDefault(item, 'primaryPhoneNumber'),
            internalId: {
                label: internalId,
                link: `/client/${internalId}`,
                state: { clientName, isProspect },
            },
            externalId: getWithDefault(item, 'customerNumber'),
            name: clientName,
            nationality: getWithDefault(findOptionById(optionCountry, String(item.countryOfNationality)), 'label'),
            country: getWithDefault(findOptionById(optionCountry, String(item.country)), 'label'),

            actions: {
                value: 'action',
                actions: [
                    {
                        text: isProspect ? t('clients.onBoardingToWealth') : t('clients.clientOverview'),
                        onClick: isProspect
                            ? onBoarding({ internalId, clientName })
                            : clientOverview({ internalId, clientName }),
                    },
                ],
            },
        };
    });
};
